import { createInvoiceReview, getInvoiceDistricts } from "../../api/services/invoiceService";
import { Fragment, useEffect, useState } from "react";
import { getDivisionOptions } from "../../api/services/divisionService";
import { InvoiceReviewDistrict } from "../../api/models/invoiceReviewDistrict";
import { InvoiceStatus, monthsOfYear, Role as RoleEnum } from "../../common/constants";
import moment, { Moment } from "moment";
import { useAsync } from "react-async";
import { useHistory } from "react-router-dom";
import InvoiceDistrictEntries from "../../components/invoiceDistrictEntries";
import InvoiceExportRequest from "../../api/requests/invoiceExportRequest";
import InvoiceReviewDistrictResponse from "../../api/responses/invoiceReviewDistrictResponse";
import InvoiceReviewDistrictsRequest from "../../api/requests/invoiceReviewDistrictsRequest";
import Layout from "../../components/layout";
import Modal from "../../components/modal";
import Role from "../../api/models/role";
import { useAppSelector } from "../../app/hooks";
import { hasRole } from "../../common/utils";
import { FieldInput } from "../../components/fieldInput";
import { option } from "../../api/models/option";
import Select from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';

// Variables & general utility functions
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const now = new Date();
const dateRangeFormat: string = 'MMM YYYY';
const invoiceStatuses = [InvoiceStatus.Pending, InvoiceStatus.Approved, InvoiceStatus.Rejected];

type MonthSelectorProps = {
    range: { start: Moment, end: Moment },
    clickHandler: Function,
}

const initializeDivisions = async () => await getDivisionOptions();

// This function serves to fill in reviewer slots on the rare occasions that a managerial row
// (Admin, Director, or Billing Specialist) does not come back from the database for that type.
const populateReviewerPlaceholders = (districts: InvoiceReviewDistrict[]) => {
    for (const district of districts) {
        if (!district.reviewers?.length) {
            district.reviewers = [];
        }
        const existingUserRoleIds = district.reviewers!.flatMap(r => r.user.userRoles.map(ur => ur.role.roleId));
        const placeholderRoles = requiredReviewerRoles.filter(r => !existingUserRoleIds.includes(r.roleId));

        placeholderRoles.forEach((elem, idx) => {
            district.reviewers!.push({
                invoiceStatusId: 1,
                reviews: [],
                user: {
                    userId: 0,
                    emailAddress: '',
                    firstName: '',
                    lastName: '',
                    isDisabled: false,
                    userRoles: [
                        {
                            userId: 0,
                            roleId: elem.roleId,
                            role: {
                                roleId: elem.roleId,
                                name: elem.name,
                            }
                        }
                    ]
                }
            })
        })
    }
}

const MonthSelector = ({ range, clickHandler }: MonthSelectorProps) => {
    return (
        <div className='mb-2 month-selector'>
            <p className='text-sm font-bold'>Currently Viewing</p>
            <div>
                <span className='text-3xl font-bold'>{range.start.format(dateRangeFormat)} </span>
                <span className='text-sm font-bold text-ptsblue-normal underline cursor-pointer hover:text-ptsblue-normal'
                    title='Change date range'
                    onClick={() => clickHandler(true)}>Change</span>
            </div>
        </div>
    );
}

const getMonthDateRange = (year: number, month: number) => {
    const moment = require('moment');
    const startDate = moment([year, month - 1]);
    const endDate = moment(startDate).endOf('month');

    return { start: startDate, end: endDate };
}

const requiredReviewerRoles: Role[] = [
    { roleId: 1, name: RoleEnum.Admin },
    { roleId: 3, name: RoleEnum.Director },
    { roleId: 5, name: RoleEnum.BillingSpecialist },
];
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~


// Main component
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const InvoiceReview = () => {
    const history = useHistory();

    const getFilterData = () => {
        let dateString = localStorage.getItem("invoiceReviewTime");
        let date = moment(dateString);

        if (date && moment().isBefore(date.add(10, 'minutes'))) {
            let filterString = localStorage.getItem("invoiceReviewFilter");
            if (filterString) {
                let filterData = JSON.parse(filterString);
                return filterData;
            }
        }

        localStorage.removeItem("invoiceReviewTime")
        localStorage.removeItem("invoiceReviewFilter")
        return null;
    }

    // States
    //---------------------------------------------------------------------------------------------
    const [month, setMonth] = useState<number>(getFilterData()?.month ?? now.getMonth() + 1);
    const [year, setYear] = useState<number>(getFilterData()?.year ?? now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(month);
    const [selectedYear, setSelectedYear] = useState<number>(year);
    const [openMonthPicker, setOpenMonthPicker] = useState<boolean>(false);
    const [isDirector, setIsDirector] = useState<boolean>(false);
    const [showErrorsOnly, setShowErrorsOnly] = useState<boolean>(false);
    const [divisions, setDivisions] = useState<option[]>();
    const [divisionIdFilter, setDivisionIdFilter] = useState<number>(getFilterData()?.division ?? undefined);
    const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<number>(getFilterData()?.status ?? undefined);
    const [readyForReviewFilter, setReadyForReviewFilter] = useState<string>(getFilterData()?.readyForReview ?? '');
    const [exportedAndLockedFilter, setExportedAndLockedFilter] = useState<string | undefined>(getFilterData()?.exported ?? '');
    const [districtNameFilter, setDistrictNameFilter] = useState<string | undefined>(getFilterData()?.district ?? '');
    const [programNameFilter, setProgramNameFilter] = useState<string | undefined>(getFilterData()?.program ?? '');
    const [invoiceDistricts, setInvoiceDistricts] = useState<InvoiceReviewDistrict[]>();
    const [visibleInvoiceDistricts, setVisibleInvoiceDistricts] = useState<InvoiceReviewDistrict[]>();
    //---------------------------------------------------------------------------------------------
    //Control vars    
    const userRoles = useAppSelector((s) => s.user.roles);

    // API operations
    //---------------------------------------------------------------------------------------------
    useEffect(() => {
        setTimeout(() => loadPageAsync.run(), 1);
        setIsDirector(hasRole(userRoles!, RoleEnum.Director));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getInvoiceReviewDistrictsDefer = async (args: any[]) => {
        if (selectedYear && selectedMonth) {
            const request: InvoiceReviewDistrictsRequest = {
                year: selectedYear,
                month: selectedMonth,
                divisionId: divisionIdFilter,
                districtName: districtNameFilter,
                programName: programNameFilter,
                invoiceStatusId: invoiceStatusFilter,
                isExportedAndLocked: JSON.parse(exportedAndLockedFilter || 'null'),
                isReadyForReview: JSON.parse(readyForReviewFilter || 'null'),
            }

            return await getInvoiceDistricts(request);
        }

        return null;
    }

    const setFilterData = () => {
        let currentDateTime = moment().toString();
        let filterData = {
            year: selectedYear,
            month: selectedMonth,
            division: divisionIdFilter ?? undefined,
            district: districtNameFilter ?? '',
            program: programNameFilter ?? '',
            status: invoiceStatusFilter ?? undefined,
            readyForReview: readyForReviewFilter ?? '',
            exported: exportedAndLockedFilter ?? '',
        }

        let filterString = JSON.stringify(filterData);
        localStorage.setItem("invoiceReviewFilter", filterString);
        localStorage.setItem("invoiceReviewTime", currentDateTime);
    }

    const loadPageAsync = useAsync({
        deferFn: getInvoiceReviewDistrictsDefer,
        onResolve: (response) => {
            const invoiceDistrictsResponse: InvoiceReviewDistrict[] =
                response?.data?.districts ?? [] as InvoiceReviewDistrict[];

            populateReviewerPlaceholders(invoiceDistrictsResponse);

            // Set states
            setShowErrorsOnly(false);
            setOpenMonthPicker(false);
            setYear(selectedYear);
            setMonth(selectedMonth);
            setFilterData();

            setInvoiceDistricts([...invoiceDistrictsResponse]); // Original copy -- do not modify

            let visibleDistricts = [...invoiceDistrictsResponse];

            switch (invoiceStatusFilter) {
                case InvoiceStatus.Pending:
                    visibleDistricts = visibleDistricts.filter(x => x.invoiceStatusId === InvoiceStatus.Pending);
                    break;
                case InvoiceStatus.Approved:
                    visibleDistricts = visibleDistricts.filter(x => x.invoiceStatusId === InvoiceStatus.Approved);
                    break;
                case InvoiceStatus.Rejected:
                    visibleDistricts = visibleDistricts.filter(x => x.invoiceStatusId === InvoiceStatus.Rejected);
                    break;
            }

            switch (readyForReviewFilter) {
                case 'true':
                    visibleDistricts = visibleDistricts.filter(x => x.isReadyForReview);
                    break;
                case 'false':
                    visibleDistricts = visibleDistricts.filter(x => !x.isReadyForReview);
                    break;
            }

            visibleDistricts = visibleDistricts.filter(x =>
                (exportedAndLockedFilter !== '' ? exportedAndLockedFilter === 'true' ? x.isExportedAndLocked : (exportedAndLockedFilter === 'false' ? !x.isExportedAndLocked : x) : x) &&
                (exportedAndLockedFilter !== '' ? exportedAndLockedFilter === 'true' ? x.isMarkedForExport : (exportedAndLockedFilter === 'false' ? !x.isMarkedForExport : x) : x));

            setVisibleInvoiceDistricts(visibleDistricts); // Displayed districts
        },
    });

    const loadDivisionsAsync = useAsync({
        promiseFn: getDivisionOptions,
        onResolve: (response) => {
            if (response.data) {
                let data = response.data.map(x => ({ value: x.value, label: x.label } as option))
                setDivisions(data)
            }
        }
    });

    const addInvoiceReviewAsync = useAsync({
        deferFn: (args: any[]) => createInvoiceReview(args[0]),
        onResolve: (response: InvoiceReviewDistrictResponse) => {
            let districtsDeepClone: InvoiceReviewDistrict[] = JSON.parse(JSON.stringify(visibleInvoiceDistricts));
            let targetIndex = districtsDeepClone?.findIndex(d => d.districtId === response.data.district.districtId && d.programId === response.data.district.programId);
            if (targetIndex !== -1) {
                districtsDeepClone[targetIndex] = response.data.district;
                populateReviewerPlaceholders(districtsDeepClone);
                setVisibleInvoiceDistricts(districtsDeepClone);
            }
        },
        onReject(error) {
            window.alert(error);
        },
    });
    //---------------------------------------------------------------------------------------------


    // In-component utility functions
    //---------------------------------------------------------------------------------------------
    const handleShowErrorsOnly = (displayErrors: boolean) => {
        const invoiceDistrictsWithErrors = visibleInvoiceDistricts?.filter(d => d.issueCount > 0);
        const visibleDistricts = (displayErrors) ? invoiceDistrictsWithErrors : invoiceDistricts;

        setShowErrorsOnly(displayErrors);
        setVisibleInvoiceDistricts(visibleDistricts);
    }

    const handleFilter = () => {
        loadPageAsync.run();
    }

    const navigateToExportsSummary = (invoiceExportRequest: InvoiceExportRequest) => {
        history.push(`/accounting/invoice/exports`, { invoiceExportRequest });
    }
    //---------------------------------------------------------------------------------------------


    // UI layout
    //---------------------------------------------------------------------------------------------
    return (
        <Layout isLoading={loadPageAsync.isLoading || loadDivisionsAsync.isLoading || addInvoiceReviewAsync.isLoading}>
            <Fragment>
                {/* Month picker modal */}
                <Modal
                    open={openMonthPicker}
                    saveText='Set Month/Year'
                    onSave={() => loadPageAsync.run()}
                    onCancel={() => setOpenMonthPicker(false)}
                >
                    <div className='flex space-x-3'>
                        <div className='w-full'>
                            <label className='mb-2'>Month:</label>
                            <Select
                                styles={graySelectStyles}
                                isSearchable={false}
                                value={monthsOfYear.map(m => ({ value: m.value, label: m.name })).find(o => o.value === selectedMonth)}
                                options={monthsOfYear.map(m => ({ value: m.value, label: m.name }))}
                                onChange={(selected) => {
                                    setSelectedMonth(selected!.value)
                                }}
                                placeholder="Month"
                            />
                        </div>
                        <div className='w-full'>
                            <label className='mb-2'>Year:</label>
                            <Select
                                styles={graySelectStyles}
                                isSearchable={false}
                                value={Array.apply(0, Array(10)).map(function (x, i) {
                                    const y = now.getFullYear() - i;
                                    return { value: y, label: String(y) };
                                }).find(o => o.value === selectedYear)}
                                options={Array.apply(0, Array(10)).map(function (x, i) {
                                    const y = now.getFullYear() - i;
                                    return { value: y, label: String(y) };
                                })}
                                onChange={(selected) => {
                                    setSelectedYear(selected!.value)
                                }}
                                placeholder="Year"
                            />
                        </div>
                    </div>
                </Modal>
                <div className='flex items-center justify-between mb-2'>
                    <div id='division-filter-wrapper' className='flex flex-col'>
                        <label htmlFor='division-filter'>Division</label>
                        <Select
                            className='block mt-1 primary w-32'
                            styles={graySelectStyles}
                            value={{ label: divisions?.find(x => x.value === divisionIdFilter)?.label, value: divisionIdFilter } as option}
                            name='test'
                            options={divisions ?? []}
                            placeholder={`Division`}
                            onChange={(e) => setDivisionIdFilter(e!.value)}
                            isSearchable={false}
                        />
                    </div>
                    <div id='district-name-filter-wrapper' className='flex flex-col w-36'>
                        <FieldInput
                            type='text'
                            value={districtNameFilter}
                            rounded='sm'
                            placeholder=''
                            label={`District`}
                            onChange={(e) => { setDistrictNameFilter(e.target.value); }}
                            icon={'search'}
                        />
                    </div>
                    <div id='district-name-filter-wrapper' className='flex flex-col w-36'>
                        <FieldInput
                            type='text'
                            value={programNameFilter}
                            rounded='sm'
                            placeholder=''
                            label={`Program`}
                            onChange={(e) => { setProgramNameFilter(e.target.value); }}
                            icon={'search'}
                        />
                    </div>
                    <div id='invoice-status-filter-wrapper' className='flex flex-col'>
                        <label htmlFor='invoice-status-filter'>Invoice Status</label>
                        <Select
                            className='block mt-1 primary w-32'
                            styles={graySelectStyles}
                            value={{ label: InvoiceStatus[invoiceStatusFilter ?? 0], value: invoiceStatusFilter } as option}
                            name='test'
                            options={invoiceStatuses.map(x => ({ value: x, label: InvoiceStatus[x] } as option)) ?? []}
                            placeholder={`Invoice Status`}
                            onChange={(e) => setInvoiceStatusFilter(e!.value)}
                            isSearchable={false}
                        />
                    </div>
                    <div id='ready-for-review-filter-wrapper' className='flex flex-col'>
                        <label htmlFor='ready-for-review-filter'>Ready For Review</label>
                        <Select
                            styles={graySelectStyles}
                            isSearchable={false}
                            value={[
                                { value: '', label: 'All' },
                                { value: 'false', label: 'No' },
                                { value: 'true', label: 'Yes' },
                            ].find(o => o.value === readyForReviewFilter)}
                            options={[
                                { value: '', label: 'All' },
                                { value: 'false', label: 'No' },
                                { value: 'true', label: 'Yes' },
                            ]}
                            onChange={(selected) => setReadyForReviewFilter(selected!.value)}
                            placeholder="Ready For Review"
                        />
                    </div>
                    {!isDirector &&
                        <div id='exported-locked-filter-wrapper' className='flex flex-col'>
                            <label htmlFor='exported-locked-filter'>Exported &amp; Locked</label>
                            <Select
                                styles={graySelectStyles}
                                isSearchable={false}
                                value={[
                                    { value: '', label: 'All' },
                                    { value: 'false', label: 'No' },
                                    { value: 'true', label: 'Yes' },
                                ].find(o => o.value === exportedAndLockedFilter)}
                                options={[
                                    { value: '', label: 'All' },
                                    { value: 'false', label: 'No' },
                                    { value: 'true', label: 'Yes' },
                                ]}
                                onChange={(selected) => setExportedAndLockedFilter(selected!.value)}
                                placeholder="Exported & Locked"
                            />
                        </div>
                    }
                    <div id='filter-button-wrapper' className='flex flex-col'>
                        <label>&nbsp;</label>
                        <button id='filter-button'
                            className='w-32'
                            onClick={handleFilter}>Filter
                        </button>
                    </div>
                    {!isDirector &&
                        <div id='errors-only-filter'>
                            <input type='checkbox' id='errors-only-checkbox' className='mx-2'
                                checked={showErrorsOnly}
                                onChange={(event) => handleShowErrorsOnly(event.target.checked)} />
                            <label htmlFor='errors-only-checkbox'>Errors Only</label>
                        </div>
                    }
                </div>

                <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow'>
                    <MonthSelector
                        range={getMonthDateRange(year, month)}
                        clickHandler={setOpenMonthPicker} />

                    {visibleInvoiceDistricts &&
                        <InvoiceDistrictEntries
                            districts={visibleInvoiceDistricts}
                            year={selectedYear}
                            month={selectedMonth}
                            onReviewCreated={addInvoiceReviewAsync.run}
                            onInvoiceExport={navigateToExportsSummary}
                            isDirector={isDirector}
                        />
                    }
                </div>

            </Fragment>
        </Layout>
    )
    //---------------------------------------------------------------------------------------------
}
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

export default InvoiceReview;
